import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert, AlertDescription } from '../components/ui/alert';
import { AlertCircle, ArrowLeft, ShieldCheck, Lock, FileText } from 'lucide-react';
import BrandLogo from '../components/BrandLogo';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || '/';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login({ email, password });
      if (user.role === 'admin') navigate('/admin/dashboard');
      else if (user.role === 'company') {
        // Manpower Agency tenants land on the slim agency dashboard
        if (user.company_category === 'manpower_agency') navigate('/agency/dashboard');
        else if (user.company_category === 'corporate') navigate('/corporate/dashboard');
        else navigate('/company/dashboard');
      }
      else if (user.role === 'candidate') navigate('/candidate/portal');
      else if (user.role === 'field_agent') navigate('/agent/dashboard');
      else navigate(from, { replace: true });
    } catch (err) {
      const raw = err.response?.data;
      const status = err.response?.status;
      const isHtmlEdgeError =
        typeof raw === 'string' &&
        (raw.includes('Cloudflare') || raw.includes('<!DOCTYPE') || raw.includes('<html'));
      if (isHtmlEdgeError || (status >= 500 && typeof raw !== 'object')) {
        setError('Login service is warming up — please wait 10 seconds and try again. If this persists, hard-refresh the page (Cmd+Shift+R / Ctrl+Shift+R).');
      } else {
        setError(raw?.detail || 'Login failed. Please check your credentials.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-brand-cream flex flex-col lg:flex-row" data-testid="login-page">
      {/* --- Left: editorial brand panel (hidden on mobile) ------------- */}
      <div className="hidden lg:flex lg:w-1/2 bg-brand-navy text-white relative overflow-hidden grain-overlay">
        <div
          aria-hidden="true"
          className="absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full pointer-events-none opacity-50"
          style={{ background: 'radial-gradient(closest-side, rgba(254,76,28,0.4), transparent 70%)' }}
        />
        <div className="relative z-10 flex flex-col justify-between p-14 xl:p-16 w-full">
          <div className="self-start">
            <BrandLogo surface="dark" showTagline markClass="h-10 w-auto" textClass="text-2xl" className="mb-6" />
            <button
              onClick={() => navigate('/')}
              className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm transition-colors"
              data-testid="login-back-home"
            >
              <ArrowLeft className="w-4 h-4" /> Back to home
            </button>
          </div>

          <div className="max-w-lg">
            <div className="eyebrow text-white/50 mb-6">
              <span className="inline-block w-8 h-px bg-white/40 mr-3 align-middle" />
              VerifyPro · by Infini10
            </div>
            <h1 className="font-display text-5xl xl:text-6xl font-medium tracking-tighter leading-[0.98] text-balance">
              Every hire, <span className="italic font-normal text-brand-orange">accounted for.</span>
            </h1>
            <p className="mt-6 text-white/60 text-base xl:text-lg leading-relaxed max-w-md">
              DPDP-compliant background verification for banks, recruiters and startups —
              seven checks, one platform, evidence that holds up in court.
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 text-xs">
            {[
              { icon: ShieldCheck, k: 'DPDP', v: 'Compliant' },
              { icon: Lock, k: 'Signed', v: 'Audit trail' },
              { icon: FileText, k: 'QR', v: 'Verifiable' },
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="border-l border-white/20 pl-3">
                  <Icon className="w-4 h-4 text-brand-orange mb-2" strokeWidth={1.5} />
                  <div className="text-white/40 uppercase tracking-widest text-[10px] font-semibold">{s.k}</div>
                  <div className="text-white/85 mt-0.5">{s.v}</div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {/* --- Right: auth form ------------------------------------------ */}
      <div className="flex-1 flex flex-col justify-center px-6 sm:px-10 lg:px-16 xl:px-24 py-12">
        <div className="w-full max-w-md mx-auto">
          <div className="lg:hidden mb-8">
            <BrandLogo surface="light" showTagline markClass="h-10 w-auto" textClass="text-2xl" />
          </div>

          <div className="mb-10">
            <div className="eyebrow mb-3">Sign in</div>
            <h2 className="font-display text-4xl font-medium tracking-tighter text-brand-ink leading-tight">
              Welcome back.
            </h2>
            <p className="mt-3 text-brand-ink/60 text-sm">
              Enter your credentials to access VerifyPro.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {error && (
              <Alert variant="destructive" data-testid="login-error-alert">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-xs uppercase tracking-widest font-semibold text-brand-ink/70">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11 border-brand-ink/15 bg-white"
                data-testid="email-input"
              />
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-xs uppercase tracking-widest font-semibold text-brand-ink/70">
                  Password
                </Label>
                <button
                  type="button"
                  onClick={() => navigate('/recover-admin')}
                  className="text-xs text-brand-ink/50 hover:text-brand-orange transition-colors"
                  data-testid="login-forgot-admin-link"
                >
                  Forgot?
                </button>
              </div>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11 border-brand-ink/15 bg-white"
                data-testid="password-input"
              />
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-brand-orange hover:bg-brand-orange-hover text-white font-semibold transition-all hover:-translate-y-[1px] shadow-soft"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? 'Signing in…' : 'Sign in →'}
            </Button>
          </form>

          <div className="mt-10 pt-6 border-t border-brand-ink/10 flex items-center justify-between text-xs text-brand-ink/50">
            <button
              type="button"
              onClick={() => navigate('/privacy-policy')}
              className="hover:text-brand-ink transition-colors underline decoration-dotted underline-offset-4"
              data-testid="login-privacy-link"
            >
              Privacy Policy
            </button>
            <span className="inline-flex items-center gap-2">© {new Date().getFullYear()} Infini10 Technologies <img src="/brand/verifypro-logo.png" alt="VerifyPro" className="h-4 w-auto opacity-80" /></span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
